/**
 * Camera keg-scan filtering: keeps the scanner fast but stops false reads
 * (dirt / scratches on a keg decoding as a bogus code).
 *
 * Two pure pieces, used by QRScanner:
 *  1. extractKegTag    – only accept decodes shaped like an Old Black Bear keg tag.
 *  2. createScanConfirmer – only accept a code after it decodes the same on N
 *     consecutive frames, then ignore that same code for a cooldown so one
 *     keg is never double-scanned. Different tags are accepted as soon as they
 *     confirm (no inter-tag gap).
 */

/**
 * Keg tag shape: "EK" + 5 digits (e.g. EK09503). Case-insensitive; every keg in
 * Tilly (1,506 imported from Ekos, EK08495–EK10543) and in Ekos keg history uses
 * this shape (a few were typed "Ek" in Ekos).
 */
export const KEG_TAG_PATTERN = /^EK\d{5}$/;

/** Same shape, used to find a tag inside a longer decode (e.g. a URL). */
const KEG_TAG_TOKEN = /(?:^|[^A-Z0-9])(EK\d{5})(?![A-Z0-9])/;

/** Frames in a row that must decode the same tag before it counts. */
export const CONFIRM_FRAMES = 2;

/**
 * After a tag is accepted, ignore that same tag for this long (ms).
 * Different tags have no gap — only this same-tag window (plus "still in view"
 * extending) prevents double-scans. Shared by QRScanner and the typed-entry
 * debounces on Keg Returns / Taproom Keg Count.
 */
export const SCAN_COOLDOWN_MS = 2500;

/**
 * Returns the normalized keg tag (uppercase "EK#####") if a decoded value looks
 * like a keg tag, otherwise null. Accepts the bare tag (any case, stray spaces)
 * or a longer payload containing exactly one tag token (e.g. ".../kegs/EK09503").
 */
export function extractKegTag(raw: string | null | undefined): string | null {
  const text = String(raw ?? '').trim().toUpperCase();
  if (!text) return null;
  const compact = text.replace(/\s+/g, '');
  if (KEG_TAG_PATTERN.test(compact)) return compact;
  const found = new Set<string>();
  const re = new RegExp(KEG_TAG_TOKEN.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) found.add(m[1]);
  return found.size === 1 ? found.values().next().value ?? null : null;
}

export interface ScanConfirmerOptions {
  /** Consecutive matching frames required (>= 1). Default CONFIRM_FRAMES. */
  requiredFrames?: number;
  /** Same-code cooldown after acceptance, ms. Default SCAN_COOLDOWN_MS. */
  cooldownMs?: number;
}

/**
 * Stateful per-frame confirmer. Call with the (already filtered) code for each
 * frame, or null when the frame decoded nothing usable. Returns the code on the
 * frame it becomes confirmed, else null.
 */
export function createScanConfirmer(opts: ScanConfirmerOptions = {}) {
  const required = Math.max(1, Math.floor(opts.requiredFrames ?? CONFIRM_FRAMES));
  const cooldownMs = Math.max(0, opts.cooldownMs ?? SCAN_COOLDOWN_MS);
  let candidate: string | null = null;
  let streak = 0;
  // Recently accepted codes -> last time they were seen confirmed.
  const recent = new Map<string, number>();

  return function feed(code: string | null, now: number = Date.now()): string | null {
    if (!code) {
      candidate = null;
      streak = 0;
      return null;
    }
    if (code === candidate) {
      streak += 1;
    } else {
      candidate = code;
      streak = 1;
    }
    if (streak < required) return null;
    // Confirmed; hold the streak so a tag left in view doesn't re-fire every frame.
    const seenAt = recent.get(code);
    recent.set(code, now); // still in view: keep extending its cooldown
    if (seenAt !== undefined && now - seenAt < cooldownMs) return null;
    if (recent.size > 50) {
      recent.forEach((t, c) => {
        if (now - t >= cooldownMs) recent.delete(c);
      });
    }
    return code;
  };
}
